import React, { useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { CustomizedTableFooter } from "../../../../components/custom/CustomizedTableFooter";
import { useDispatch, useSelector } from "react-redux";
import {
  uiCloseErrorAlert,
  uiCloseSuccessAlert,
} from "../../../../redux/actions/uiActions";
import { MyAlert } from "../../../../components/custom/MyAlert";
import { AppState } from "../../../../redux/reducers/rootReducer";
import {
  startDeleteSpecialDay,
  startLoadSpecialDays,
} from "../../../../redux/actions/entitiesActions";
import Swal from "sweetalert2";
import { useIntl, FormattedMessage } from "react-intl";

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
];

interface Props {
  page: number;
  rowsPerPage: number;
}

export const SpecialDaysTable: React.FC<Props> = ({ page, rowsPerPage }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { successAlert, successMsg, errorAlert, errorMsg } = useSelector(
    (state: AppState) => state.ui
  );
  const specialDays = useSelector(
    (state: AppState) => state.entities.specialDays
  );

  useEffect(() => {
    dispatch<any>(startLoadSpecialDays());
  }, [dispatch]);

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: intl.formatMessage({ id: "DeleteSpecialDayTitle" }),
      text: intl.formatMessage({ id: "DeleteSpecialDayMsg" }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: intl.formatMessage({ id: "YesDelete" }),
      cancelButtonText: intl.formatMessage({ id: "Cancel" }),
      customClass: {
        container: "swal-container-z-index",
      },
    });

    if (result.isConfirmed) {
      await dispatch<any>(startDeleteSpecialDay(id));
    }
  };

  const formatDate = (iso: string) => {
    const [year, month, day] = iso.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);

    const dayNum = localDate.getDate();
    const monthName = MONTHS_ES[localDate.getMonth()];
    const yearNum = localDate.getFullYear();
    return `${dayNum} de ${monthName} de ${yearNum}`;
  };

  const getYear = (iso: string) => new Date(iso).getFullYear().toString();

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, specialDays.length - page * rowsPerPage);

  const sortedDays = [...specialDays].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const paginatedData = sortedDays.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box mt={4}>
      <Typography variant="subtitle1" gutterBottom>
        <FormattedMessage id="ConfiguredDays" />
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong><FormattedMessage id="Year" /></strong></TableCell>
              <TableCell><strong><FormattedMessage id="Date" /></strong></TableCell>
              <TableCell><strong><FormattedMessage id="DayType" /></strong></TableCell>
              <TableCell><strong><FormattedMessage id="Actions" /></strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{getYear(item.date)}</TableCell>
                <TableCell>{formatDate(item.date)}</TableCell>
                <TableCell>
                  <FormattedMessage
                    id={`SpecialDay.${item.type}`}
                    defaultMessage={item.type}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDelete(item.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={4} />
              </TableRow>
            )}
          </TableBody>
          <CustomizedTableFooter
            count={specialDays.length}
            rowsPerPage={rowsPerPage}
            page={page}
          />
        </Table>
      </TableContainer>

      <MyAlert
        open={successAlert}
        typeAlert="success"
        message={successMsg ?? ""}
        time={2000}
        handleClose={() => dispatch(uiCloseSuccessAlert())}
      />

      <MyAlert
        open={errorAlert}
        typeAlert="error"
        message={errorMsg ?? ""}
        time={3000}
        handleClose={() => dispatch(uiCloseErrorAlert())}
      />
    </Box>
  );
};
